// ---------------------------------------------------------
// Interfaces de Rankings
// ---------------------------------------------------------

export interface JugadorRanking {
  idJugador: number;
  nombreJugador: string;
  posicion: number; // Posición en el ordenamiento (1 = mejor)
  puntosAsignados: number; // Puntos ingresados manualmente
  idCompetencia: number;
  tipoCompetencia: "Torneo" | "Liga";
  nombreCompetencia: string;
  categoria: string;
  genero: string;
  fecha: string;
}

export interface JugadorParaFinalizar {
  idJugador: number;
  nombreJugador: string;
  ordenMerito: number; // Orden por mérito (1 = mejor)
  descripcionMerito: string; // "Campeón", "Finalista", "1º", etc.
  puntosAsignados: number; // Input manual, inicializado en 0
}

export interface RankingAnual {
  año: number;
  categoria: string;
  genero: string;
  jugadores: Array<{
    idJugador: number;
    nombreJugador: string;
    puntosTotal: number;
    competencias: Array<{
      idCompetencia: number;
      nombreCompetencia: string;
      tipoCompetencia: "Torneo" | "Liga";
      posicion: number;
      puntos: number;
      fecha: string;
    }>;
  }>;
}

// ---------------------------------------------------------
// Obtener jugadores ordenados por mérito de TORNEO (CORREGIDO)
// ---------------------------------------------------------
export function obtenerJugadoresOrdenadosTorneo(
  torneo: any,
  nombreCompetencia: string,
  categoria: string,
  genero: string
): JugadorParaFinalizar[] {

  const jugadores: JugadorParaFinalizar[] = [];
  const jugadoresYaAgregados = new Set<number>();

  if (!torneo.rondas || torneo.rondas.length === 0) {
    return [];
  }

  const totalRondas = torneo.rondas.length;

  // ---------------------------------------------------------
  // Detectar resultados en zonas
  // ---------------------------------------------------------
  const hayResultadosEnZonas = torneo.zonas?.some((zona: any) =>
    zona.partidos?.some((p: any) =>
      p.sets?.every(
        (s: any) =>
          s.gamesLocal !== null &&
          s.gamesVisitante !== null
      )
    )
  );

  // ---------------------------------------------------------
  // Detectar resultados reales de eliminación
  // ---------------------------------------------------------
  const hayResultadosEliminacion = torneo.rondas.some((ronda: any) =>
    ronda.partidos?.some((p: any) => p.ganador)
  );

  // ---------------------------------------------------------
  // Helper para agregar jugadores
  // ---------------------------------------------------------
  const agregarJugador = (
    equipo: any,
    orden: number,
    descripcion: string
  ) => {
    if (!equipo) return;

    let ids = equipo.ids;

    if (!ids && torneo.zonas) {
      for (const zona of torneo.zonas) {
        const equipoEnZona = zona.equipos.find((e: any) => e.id === equipo.id);
        if (equipoEnZona?.ids) {
          ids = equipoEnZona.ids;
          break;
        }
      }
    }

    if (!ids) return;

    if (ids.idJugador1 && !jugadoresYaAgregados.has(ids.idJugador1)) {
      jugadores.push({
        idJugador: ids.idJugador1,
        nombreJugador: extraerNombreJugador(equipo.nombre, 1),
        ordenMerito: orden,
        descripcionMerito: descripcion,
        puntosAsignados: 0,
      });
      jugadoresYaAgregados.add(ids.idJugador1);
    }

    if (ids.idJugador2 && !jugadoresYaAgregados.has(ids.idJugador2)) {
      jugadores.push({
        idJugador: ids.idJugador2,
        nombreJugador: extraerNombreJugador(equipo.nombre, 2),
        ordenMerito: orden,
        descripcionMerito: descripcion,
        puntosAsignados: 0,
      });
      jugadoresYaAgregados.add(ids.idJugador2);
    }
  };

  // =========================================================
  // CASO 1: ZONAS JUGADAS + ELIMINACIÓN SIN RESULTADOS
  // =========================================================
  if (hayResultadosEnZonas && !hayResultadosEliminacion && torneo.zonas) {
    let ordenActual = 1;

    const tablaGeneral: any[] = [];

    torneo.zonas.forEach((zona: any) => {
      const tablaZona = calcularTablaPosicionesZona(zona);

      tablaZona.forEach((fila: any, index: number) => {
        tablaGeneral.push({
          equipo: fila.equipo,
          posicionZona: index + 1,
          zona: zona.nombre,
        });
      });
    });

    // Ordenar por mérito (posición en zona)
    tablaGeneral.sort(
      (a, b) => a.posicionZona - b.posicionZona
    );

    let posicionAnterior: number | null = null;

    tablaGeneral.forEach((item) => {
      if (posicionAnterior !== item.posicionZona) {
        posicionAnterior = item.posicionZona;
        if (ordenActual !== 1) ordenActual++;
      }

      agregarJugador(
        item.equipo,
        ordenActual,
        `${item.posicionZona}º Zona ${item.zona}`
      );
    });

    return jugadores;
  }

  // =========================================================
  // CASO 2: ELIMINACIÓN (con o sin zonas)
  // =========================================================
  let ordenActual = 1;

  // ---------------------------
  // FINAL / CAMPEÓN
  // ---------------------------
  const ultimaRonda = torneo.rondas[totalRondas - 1];

  if (
    ultimaRonda.partidos.length === 1 &&
    ultimaRonda.partidos[0].ganador
  ) {
    const final = ultimaRonda.partidos[0];

    // Campeón
    agregarJugador(final.ganador, ordenActual, "Campeón");
    ordenActual++;

    // Finalista
    const perdedor =
      final.ganador.id === final.local?.id
        ? final.visitante
        : final.local;

    agregarJugador(perdedor, ordenActual, "Finalista");
    ordenActual++;
  }

  // ---------------------------
  // RESTO DE RONDAS (de atrás hacia adelante)
  // ---------------------------
  for (let r = totalRondas - 2; r >= 0; r--) {
    const ronda = torneo.rondas[r];
    let alguienEliminado = false;

    ronda.partidos.forEach((partido: any) => {
      if (partido.ganador && partido.local && partido.visitante) {
        const perdedor =
          partido.ganador.id === partido.local.id
            ? partido.visitante
            : partido.local;

        agregarJugador(
          perdedor,
          ordenActual,
          `Ronda ${ronda.numero ?? r + 1}`
        );

        alguienEliminado = true;
      }
    });

    if (alguienEliminado) {
      ordenActual++;
    }
  }

  // ---------------------------
  // NO CLASIFICADOS (ZONAS)
  // ---------------------------
  if (torneo.zonas) {
    torneo.zonas.forEach((zona: any) => {
      const tabla = calcularTablaPosicionesZona(zona);

      tabla.forEach((fila: any, index: number) => {
        const equipo = fila.equipo;

        if (
          equipo.ids &&
          !jugadoresYaAgregados.has(equipo.ids.idJugador1)
        ) {
          agregarJugador(
            equipo,
            ordenActual,
            `No Clasificado - ${index + 1}º ${zona.nombre}`
          );
        }
      });
    });
  }

  return jugadores;
}

// ---------------------------------------------------------
// Helper: Calcular tabla de posiciones de una zona
// ---------------------------------------------------------
function calcularTablaPosicionesZona(zona: any): any[] {
  const mapa = new Map();
  
  zona.equipos.forEach((eq: any) =>
    mapa.set(eq.id, {
      equipo: eq,
      puntos: 0,
      setsGanados: 0,
      setsPerdidos: 0,
      gamesGanados: 0,
      gamesPerdidos: 0,
      pj: 0,
    })
  );

  zona.partidos?.forEach((p: any) => {
    if (!p.sets || p.sets.length < 2) return;
    
    const todosLosSetsTienenResultado = p.sets.every(
      (s: any) => 
        s.gamesLocal !== null && 
        s.gamesLocal !== undefined &&
        s.gamesVisitante !== null && 
        s.gamesVisitante !== undefined
    );
    
    if (!todosLosSetsTienenResultado) return;
    
    const local = mapa.get(p.local.id);
    const visitante = mapa.get(p.visitante.id);
    
    if (!local || !visitante) return;
    
    local.pj++;
    visitante.pj++;
    
    let setsLocal = 0,
      setsVisitante = 0;
    
    p.sets.forEach((s: any) => {
      local.gamesGanados += s.gamesLocal;
      local.gamesPerdidos += s.gamesVisitante;
      visitante.gamesGanados += s.gamesVisitante;
      visitante.gamesPerdidos += s.gamesLocal;
      
      if (s.gamesLocal > s.gamesVisitante) {
        setsLocal++;
      } else if (s.gamesVisitante > s.gamesLocal) {
        setsVisitante++;
      }
    });
    
    local.setsGanados += setsLocal;
    local.setsPerdidos += setsVisitante;
    visitante.setsGanados += setsVisitante;
    visitante.setsPerdidos += setsLocal;
    
    if (setsLocal > setsVisitante) {
      local.puntos += 3;
    } else if (setsVisitante > setsLocal) {
      visitante.puntos += 3;
    } else {
      local.puntos += 1;
      visitante.puntos += 1;
    }
  });

  const arr = Array.from(mapa.values());
  
  arr.sort((a: any, b: any) => {
    if (b.puntos !== a.puntos) return b.puntos - a.puntos;
    const aSetDiff = a.setsGanados - a.setsPerdidos || 0;
    const bSetDiff = b.setsGanados - b.setsPerdidos || 0;
    if (bSetDiff !== aSetDiff) return bSetDiff - aSetDiff;
    const aGameDiff = a.gamesGanados - a.gamesPerdidos || 0;
    const bGameDiff = b.gamesGanados - b.gamesPerdidos || 0;
    if (bGameDiff !== aGameDiff) return bGameDiff - aGameDiff;
    return a.equipo.nombre.localeCompare(b.equipo.nombre);
  });
  
  return arr;
}

// ---------------------------------------------------------
// Obtener jugadores ordenados por mérito de LIGA
// ---------------------------------------------------------
export function obtenerJugadoresOrdenadosLiga(
  liga: any,
  tablaPosiciones: any[],
  nombreCompetencia: string,
  categoria: string,
  genero: string
): JugadorParaFinalizar[] {
  const jugadores: JugadorParaFinalizar[] = [];

  tablaPosiciones.forEach((fila: any, index: number) => {
    const posicion = index + 1;
    const equipo = fila.equipo;
    
    let descripcion = `${posicion}º`;
    if (posicion === 1) descripcion = "1º - Campeón";
    else if (posicion === 2) descripcion = "2º - Subcampeón";
    else if (posicion === 3) descripcion = "3º";

    // Solo agregar si tiene jugadores asignados
    if (equipo.ids) {
      // Jugador 1
      if (equipo.ids.idJugador1) {
        jugadores.push({
          idJugador: equipo.ids.idJugador1,
          nombreJugador: extraerNombreJugador(equipo.nombre, 1),
          ordenMerito: posicion,
          descripcionMerito: descripcion,
          puntosAsignados: 0,
        });
      }

      // Jugador 2 (dobles)
      if (equipo.ids.idJugador2) {
        jugadores.push({
          idJugador: equipo.ids.idJugador2,
          nombreJugador: extraerNombreJugador(equipo.nombre, 2),
          ordenMerito: posicion,
          descripcionMerito: descripcion,
          puntosAsignados: 0,
        });
      }
    }
  });

  return jugadores;
}

// ---------------------------------------------------------
// Helper: Extraer nombre de jugador del nombre del equipo
// ---------------------------------------------------------
function extraerNombreJugador(nombreEquipo: string, numero: 1 | 2): string {
  if (nombreEquipo.includes(" - ")) {
    const partes = nombreEquipo.split(" - ");
    return numero === 1 ? partes[0].trim() : partes[1].trim();
  } else {
    return nombreEquipo;
  }
}

// ---------------------------------------------------------
// Calcular Ranking Anual (con datos pre-filtrados del backend)
// ---------------------------------------------------------
export function calcularRankingAnual(
  competenciasFinalizadas: JugadorRanking[]
): RankingAnual {
  // Los datos ya vienen filtrados por año, categoría y género desde el backend
  
  if (competenciasFinalizadas.length === 0) {
    return {
      año: new Date().getFullYear(),
      categoria: '',
      genero: '',
      jugadores: []
    };
  }

  // Obtener año, categoría y género del primer registro (todos son iguales)
  const primerRegistro = competenciasFinalizadas[0];
  const año = new Date(primerRegistro.fecha).getFullYear();
  const categoria = primerRegistro.categoria;
  const genero = primerRegistro.genero;

  // Agrupar por jugador
  const jugadoresMapa = new Map<number, any>();

  competenciasFinalizadas.forEach((registro) => {
    if (!jugadoresMapa.has(registro.idJugador)) {
      jugadoresMapa.set(registro.idJugador, {
        idJugador: registro.idJugador,
        nombreJugador: registro.nombreJugador,
        puntosTotal: 0,
        competencias: [],
      });
    }

    const jugador = jugadoresMapa.get(registro.idJugador)!;
    jugador.puntosTotal += registro.puntosAsignados;
    jugador.competencias.push({
      idCompetencia: registro.idCompetencia,
      nombreCompetencia: registro.nombreCompetencia,
      tipoCompetencia: registro.tipoCompetencia,
      posicion: registro.posicion,
      puntos: registro.puntosAsignados,
      fecha: registro.fecha,
    });
  });

  // Convertir a array y ordenar por puntos
  const jugadoresArray = Array.from(jugadoresMapa.values()).sort(
    (a, b) => b.puntosTotal - a.puntosTotal
  );

  return {
    año,
    categoria,
    genero,
    jugadores: jugadoresArray,
  };
}

// ---------------------------------------------------------
// Validar si una competencia puede finalizarse
// ---------------------------------------------------------
export function puedeFinalizarTorneo(torneo: any): { puede: boolean; razon?: string } {
//   if (!torneo.rondas || torneo.rondas.length === 0) {
//     return { puede: false, razon: "El torneo no tiene rondas generadas." };
//   }

//   // Permitir finalizar en cualquier estado si hay al menos algunos partidos jugados
//   // o si hay equipos clasificados
  
//   // Verificar si hay al menos algún partido con resultado en fase de eliminación
//   const hayAlgunResultadoEliminacion = torneo.rondas.some((ronda: any) =>
//     ronda.partidos.some((partido: any) => partido.ganador)
//   );
  
//   // Verificar si hay equipos con IDs asignados (en zonas o en eliminación)
//   let hayEquiposConJugadores = false;
  
//   if (torneo.zonas) {
//     hayEquiposConJugadores = torneo.zonas.some((zona: any) =>
//       zona.equipos.some((equipo: any) => equipo.ids)
//     );
//   } else {
//     // Torneo de eliminación directa
//     hayEquiposConJugadores = torneo.rondas[0]?.partidos.some((partido: any) =>
//       (partido.local?.ids || partido.visitante?.ids)
//     );
//   }
  
//   if (!hayEquiposConJugadores) {
//     return { puede: false, razon: "No hay jugadores asignados en el torneo." };
//   }

  return { puede: true };
}

export function puedeFinalizarLiga(liga: any, progreso: any): { puede: boolean; razon?: string } {
  // Permitir finalizar incluso si no está completa
  
  // Verificar si hay al menos algún partido jugado
//   if (progreso.partidosJugados === 0) {
//     return {
//       puede: false,
//       razon: "No hay partidos jugados. Debe jugar al menos un partido para poder finalizar.",
//     };
//   }
  
//   // Verificar si hay equipos con jugadores asignados
//   const hayEquiposConJugadores = liga.equipos.some((equipo: any) => equipo.ids);
  
//   if (!hayEquiposConJugadores) {
//     return { puede: false, razon: "No hay jugadores asignados en la liga." };
//   }

  return { puede: true };
}